import { Modal, notification } from 'antd';
import { useState } from 'react';
import APIClient from '../api/api';
import { Button } from './ui';

interface Props {
  onClose: () => void;
}

type FileType = 'dan' | 'sedo' | 'atom';
type AccountKey = 'dan' | 'squadhelp' | 'parity-software' | 'parity-wholesale';

const fileTypeOptions: { value: FileType; label: string }[] = [
  { value: 'dan', label: 'Dan' },
  { value: 'sedo', label: 'Sedo' },
  { value: 'atom', label: 'Atom / Squadhelp' },
];

const accountOptions: { value: AccountKey; label: string }[] = [
  { value: 'dan', label: 'dan' },
  { value: 'squadhelp', label: 'squadhelp' },
  { value: 'parity-software', label: 'parity-software (sedo)' },
  { value: 'parity-wholesale', label: 'parity-wholesale (sedo)' },
];

/**
 * Sales history has a venue-specific file type and account, so it can't use
 * the generic drop-action URL pattern. Submits via APIClient.uploadSalesHistory.
 */
export function SalesHistoryImportModal({ onClose }: Props) {
  const [fileType, setFileType] = useState<FileType | ''>('');
  const [account, setAccount] = useState<AccountKey | ''>('');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const canSubmit = !!fileType && !!account && !!file && !uploading;

  async function onSubmit() {
    if (!fileType || !account || !file) return;
    try {
      setUploading(true);
      const resp = await APIClient.uploadSalesHistory({ file, fileType, account });
      notification.success({
        message: `${resp.recordsLoaded.toLocaleString()} records loaded.`,
      });
      onClose();
    } catch {
      notification.error({ message: 'Failed to upload sales history.' });
    } finally {
      setUploading(false);
    }
  }

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={520}
      closable={false}
      destroyOnClose
      styles={{
        content: { padding: 0, background: 'rgb(var(--color-panel) / 1)', borderRadius: 10, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <div className="px-6 pt-5 pb-4 border-b border-border bg-panel flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-text-3 font-semibold mb-1">Import</div>
          <div className="text-text text-[17px] font-semibold leading-tight">Import Sales History</div>
          <div className="text-text-3 text-[12.5px] mt-1">Load sales records from a venue export file.</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-[28px] h-[28px] rounded-md border border-border bg-panel flex items-center justify-center cursor-pointer text-text-3 hover:bg-bg-elevated hover:text-text flex-shrink-0"
        >
          ✕
        </button>
      </div>
      <div className="px-6 py-5 bg-bg flex flex-col gap-4">
        <Field label="File Type">
          <select
            value={fileType}
            onChange={(e) => setFileType(e.target.value as FileType | '')}
            className="bg-bg-elevated border border-border rounded-md px-3 py-1.5 w-full text-[12.5px] text-text outline-none focus:border-border-strong"
          >
            <option value="">Select…</option>
            {fileTypeOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
        <Field label="Account">
          <select
            value={account}
            onChange={(e) => setAccount(e.target.value as AccountKey | '')}
            className="bg-bg-elevated border border-border rounded-md px-3 py-1.5 w-full text-[12.5px] text-text outline-none focus:border-border-strong"
          >
            <option value="">Select…</option>
            {accountOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </Field>
        <Field label="File">
          <input
            type="file"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="block w-full text-[12.5px] text-text-2 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-bg-elevated file:text-text-2 file:text-[12.5px] file:font-medium file:cursor-pointer hover:file:bg-border"
          />
        </Field>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose} disabled={uploading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={!canSubmit} loading={uploading}>
            Upload
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase tracking-wider text-text-3 font-semibold">{label}</label>
      {children}
    </div>
  );
}
